<script setup lang="ts">
import { useData, useRoute } from "vitepress";
import { inject } from "vue";
import { useNav } from "../composables/useNav";
import { Icon } from "../primitives/icons";

const { site, theme, isDark } = useData();
const { isScreenOpen, toggleScreen } = useNav();
const route = useRoute();

const toggleAppearance = inject("toggle-appearance", () => {
  isDark.value = !isDark.value;
});

function isActive(link: string): boolean {
  return route.path.startsWith(link);
}
</script>

<template>
  <header class="my-nav">
    <div class="my-nav-container">
      <a href="/" class="my-nav-title">{{ site.title }}</a>

      <nav class="my-nav-links" aria-label="Main navigation">
        <a
          v-for="link in theme.nav"
          :key="link.link"
          :href="link.link"
          class="my-nav-link"
          :class="{ active: isActive(link.link) }"
        >
          {{ link.text }}
        </a>
      </nav>

      <div class="my-nav-actions">
        <button
          class="my-nav-appearance"
          type="button"
          :aria-label="
            isDark ? 'Switch to light theme' : 'Switch to dark theme'
          "
          @click="toggleAppearance"
        >
          <Icon v-if="isDark" name="sun" :size="20" />
          <Icon v-else name="moon" :size="20" />
        </button>

        <button
          class="my-nav-hamburger"
          type="button"
          :aria-expanded="isScreenOpen"
          aria-controls="nav-screen"
          aria-label="Toggle navigation menu"
          @click="toggleScreen"
        >
          <span class="hamburger-line" />
          <span class="hamburger-line" />
          <span class="hamburger-line" />
        </button>
      </div>
    </div>
  </header>
</template>

<style lang="scss" scoped>
@use "../styles/abstracts/breakpoints" as *;
@use "../styles/abstracts/mixins";

.my-nav {
  position: sticky;
  top: 0;
  z-index: 100;
  backdrop-filter: blur(40px);
  background-color: var(--color-background-mute);
  border-bottom: 1px solid var(--color-text-muted);
}

.my-nav-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: var(--content-width-wide);
  margin: 0 auto;
  padding: 0 var(--space-lg);
  height: var(--nav-height, 64px);
}

.my-nav-title {
  text-decoration: none;

  &:hover,
  &:focus {
    text-decoration: underline;
  }
}

.my-nav-links {
  display: none;
  gap: var(--space-lg);
  margin-left: auto;

  @include lg {
    display: flex;
  }
}

.my-nav-link {
  position: relative;
  padding: var(--space-sm) var(--space-md);
  color: var(--color-text);
  text-decoration: none;
  font-weight: 500;

  &::after {
    content: "";
    position: absolute;
    left: var(--space-md);
    right: var(--space-md);
    bottom: 0;
    height: 2px;
    background-color: var(--color-accent);
    transform: scaleX(0);
    transform-origin: center;
    transition: transform 0.25s ease-out;
  }

  &:hover,
  &:focus,
  &.active {
    &::after {
      transform: scaleX(1);
    }
  }
}

.my-nav-actions {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
}

.my-nav-appearance {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--color-text);
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.2s;

  &:hover,
  &:focus {
    background-color: var(--color-background-soft);
  }
}

.my-nav-hamburger {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  width: 40px;
  height: 40px;
  padding: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.2s;
  background-color: var(--color-text);

  @include lg {
    display: none;
  }

  &:hover,
  &:focus {
    background-color: var(--color-background-soft);
  }
}

.hamburger-line {
  display: block;
  width: 100%;
  height: 2px;
  background-color: currentColor;
  border-radius: 1px;
  transition:
    transform 0.2s,
    opacity 0.2s;
}
</style>
